/**
 * SideScreen: SIDE 모드 — 메인 영상 + 하단 미니 목록 레이아웃.
 *
 * v1 원본: RenewalComponents/SVContent/SideScreen/SideScreenContainer.js
 *          + SideScreenPresenter.js
 * v2 변환: 단일 함수형 컴포넌트
 *
 * 구조: 메인 비디오(왼쪽/상단) + 서브 비디오 리스트(우측/하단 수평 스크롤)
 * isExpand: 서브 리스트 접힘 상태
 */
import React, { useState, useCallback, useMemo } from 'react';
import { useAppSelector } from '@/store/hooks';
import SideMainVideo from './SideMainVideo';
import SideSubVideoList from './SideSubVideoList';
import { isRecorderMode } from '@/utils/recorderMode';


interface SideScreenProps {
  localUserId: string;
  onSelectParticipant?: (userId: string | null) => void;
}

const SideScreen: React.FC<SideScreenProps> = ({ localUserId, onSelectParticipant }) => {
  const [isExpand, setIsExpand] = useState(false);
  const mainUser = useAppSelector((s) => s.mainUser);
  const participants = useAppSelector((s) => s.participants);
  const whiteboard = useAppSelector((s) => s.whiteboard);

  const isCoachMarkActive =
    whiteboard.isOpen &&
    whiteboard.isSketchMode &&
    whiteboard.isOwner &&
    !whiteboard.isSketchCoachMarkShown;

  const handleExpand = useCallback(() => setIsExpand(true), []);
  const handleCollapse = useCallback(() => setIsExpand(false), []);

  // 메인 유저 결정: pinnedUserId → mainUserId → 첫 번째 가시 원격 참여자
  const resolvedMainUserId = useMemo(() => {
    // recorder 모드: 자기 자신은 메인이 될 수 없다. 원격 참여자만 대상으로 하고
    // 없으면 ''(메인 없음)을 반환한다.
    const recorder = isRecorderMode();

    const pinned = mainUser.pinnedUserId;
    if (pinned && (participants.participantIds.includes(pinned) || (!recorder && pinned === localUserId))) {
      return pinned;
    }
    const main = mainUser.mainUserId;
    if (main && (participants.participantIds.includes(main) || (!recorder && main === localUserId))) {
      return main;
    }
    // 첫 번째 가시 원격 참여자 (hidden/jigasi 제외)
    const firstVisible = participants.participantIds.find((id) => {
      const p = participants.participants[id];
      return p && !p.isHidden && !p.properties?.features_jigasi;
    });
    return firstVisible ?? (recorder ? '' : localUserId);
  }, [mainUser.pinnedUserId, mainUser.mainUserId, participants.participantIds, participants.participants, localUserId]);

  const hasOtherUsers = participants.participantIds.length > 0;

  return (
    <div className={`usermov_wrap usermov_wrapNew${resolvedMainUserId === "whiteboard" ? ' usermov_sketch' : ''}`}>
      <div className={`user_mov customView sideView${isExpand ? ' sideView_expand' : ''}`}>
        <SideMainVideo
          localUserId={localUserId}
          mainUserId={resolvedMainUserId}
          isExpand={isExpand}
          onExpand={handleExpand}
          onCollapse={handleCollapse}
          hasOtherUsers={hasOtherUsers}
        />
        {!isExpand && (
          <SideSubVideoList
            localUserId={localUserId}
            mainUserId={resolvedMainUserId}
          />
        )}
      </div>
    </div>
  );
};

export default SideScreen;
